"use client";

/**
 * useAcceleratorsAnnouncementBanner
 *
 * Drives the Accelerators announcement banner on the signed-in home page:
 * decides whether a returning founder sees it, and owns its one dismissal.
 * See src/lib/accelerators/announcementBanner.ts for the dismissal-key, copy
 * and deep-link rules this hook dispatches.
 *
 * Requires BOTH `accelerators-announcement` and `accelerators-row`: the
 * button opens the Accelerators row's Programs block, which only exists with
 * the row on. `useFeatureFlag` already coerces a loading/unknown read to
 * false, so either flag failing to resolve hides the banner rather than
 * flashing it.
 *
 * "Returning founder" is computed only from projects the viewer owns and
 * that are neither archived nor deleted (ownedActiveProjects) — a
 * collaborator with no project of their own never counts, and
 * archiving/deleting a project bumps its `updatedAt`, so an unfiltered list
 * could read that as the founder's most recent work. Clerk's `isLoaded` is
 * also required: before it resolves, `user` is undefined, which reads as
 * "not dismissed" — showing the banner in that gap, or a dismiss landing in
 * it, would be wrong (dismissing a not-yet-known state saves nothing real).
 *
 * Dismissal also checks a per-user sessionStorage marker alongside Clerk
 * metadata: the in-mount override alone does not survive a remount (opening
 * a project and coming back to `/`), so a failed `user.update` would show
 * the banner again within the same session. The marker is written on every
 * dismiss, before the Clerk write is even attempted, so it covers the
 * write's latency too, not just its failure.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useUser } from "@clerk/nextjs";

import {
  acceleratorsAnnouncementHref,
  isAcceleratorsAnnouncementDismissed,
  isAcceleratorsAnnouncementSessionDismissed,
  markAcceleratorsAnnouncementSessionDismissed,
  mostRecentlyUpdatedProjectId,
  ownedActiveProjects,
  withAcceleratorsAnnouncementDismissed,
  type AcceleratorsAnnouncementProject,
} from "@/lib/accelerators/announcementBanner";
import {
  trackAcceleratorAnnouncementClicked,
  trackAcceleratorAnnouncementDismissed,
  trackAcceleratorAnnouncementShown,
} from "@/lib/analytics/client";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/analytics/featureFlags";
import { captureClientError } from "@/lib/sentry/capture-client";

export interface AcceleratorsAnnouncementBannerState {
  visible: boolean;
  /** Only meaningful while `visible`; "/" when there is no project to link to. */
  href: string;
  onButtonClick: () => void;
  onDismiss: () => void;
}

export function useAcceleratorsAnnouncementBanner({
  projects,
  viewerUserId,
}: {
  projects: ReadonlyArray<AcceleratorsAnnouncementProject>;
  /** The signed-in viewer's app user id — ownership is checked against this, never guessed. */
  viewerUserId: string | undefined;
}): AcceleratorsAnnouncementBannerState {
  const announcementOn = useFeatureFlag(FEATURE_FLAGS.ACCELERATORS_ANNOUNCEMENT);
  const rowOn = useFeatureFlag(FEATURE_FLAGS.ACCELERATORS_ROW);
  const { isLoaded, user } = useUser();

  // Optimistic: set before the Clerk write even starts, so a slow or failed
  // write never leaves the banner showing again after the founder dismissed it.
  const [dismissedOverride, setDismissedOverride] = useState(false);

  const dismissed =
    dismissedOverride ||
    isAcceleratorsAnnouncementSessionDismissed(user?.id) ||
    isAcceleratorsAnnouncementDismissed(user?.unsafeMetadata);
  const projectId = mostRecentlyUpdatedProjectId(ownedActiveProjects(projects, viewerUserId));
  const visible = isLoaded && announcementOn && rowOn && projectId !== null && !dismissed;

  const shownRef = useRef(false);
  useEffect(() => {
    if (!visible || !projectId || shownRef.current) return;
    shownRef.current = true;
    trackAcceleratorAnnouncementShown({ projectId });
  }, [visible, projectId]);

  const onButtonClick = useCallback(() => {
    if (!projectId) return;
    trackAcceleratorAnnouncementClicked({ projectId });
  }, [projectId]);

  const onDismiss = useCallback(() => {
    if (dismissed || !projectId) return;
    setDismissedOverride(true);
    // Before the Clerk write even starts: the sessionStorage marker is the
    // backstop for that write's own latency or outright failure, not only
    // for its failure, so it must be in place before either can happen.
    markAcceleratorsAnnouncementSessionDismissed(user?.id);
    trackAcceleratorAnnouncementDismissed({ projectId });
    if (!user) return;
    const merged = withAcceleratorsAnnouncementDismissed(
      user.unsafeMetadata as Record<string, unknown> | null | undefined,
    );
    user.update({ unsafeMetadata: merged }).catch((err: unknown) => {
      // The override above already hid the banner for this session; a
      // failed write only loses the "stays dismissed next session" promise,
      // not anything the founder is doing right now.
      captureClientError(err, {
        feature: "accelerators-announcement",
        errorType: "dismiss-metadata-write-failed",
        impact: "degraded",
      });
    });
  }, [dismissed, projectId, user]);

  return {
    visible,
    href: projectId ? acceleratorsAnnouncementHref(projectId) : "/",
    onButtonClick,
    onDismiss,
  };
}
